/**
 * Pan and zoom, as arithmetic.
 *
 * The canvas is 585×559 and every interesting edit is a few texels wide — a
 * seam, a hem, the two-pixel gutter the export dilates into. Fitted to a laptop
 * pane the whole template is about one screen pixel per texel, so the work this
 * tool exists for could not be seen while it was being done.
 *
 * ## The transform goes on a wrapper, not on the context
 *
 * Everything here describes a CSS transform applied to the element the canvas
 * sits in: `translate(x, y) scale(scale)` with `transform-origin: 0 0`. It is
 * deliberately NOT a transform on the 2D context.
 *
 * That is the decision the rest of the editor rests on. `toTemplate()` maps a
 * pointer to a texel by straight ratio:
 *
 *     ((e.clientX - rect.left) / rect.width) * canvas.width
 *
 * and `getBoundingClientRect()` reports the box *after* transforms — measured
 * in a real browser, not assumed: a 200×100 element under `scale(3)` reports
 * 600×300, and the origin moves by the translate. So every existing pointer
 * mapping, the brush ring and the face picker's hit test keep working with no
 * change at all. Transforming the drawing context would have broken all three
 * and left the artwork itself at the wrong resolution.
 *
 * ## Screen and content
 *
 * Two coordinate spaces, and mixing them up is the whole difficulty:
 *
 *   **content** — texels, 0..585 × 0..559. What the artwork is in.
 *   **screen**  — CSS pixels relative to the viewport element's top-left.
 *
 *     screen = content * scale + offset
 *     content = (screen - offset) / scale
 *
 * Everything below is one of those two lines with a constraint attached, and
 * all of it is pure so it can be tested without a browser — which matters here
 * more than usual, because the failure mode of a wrong sign is a canvas that
 * drifts away under the cursor and no error anywhere.
 */

/** The whole view state. Serialisable, comparable, and cheap to copy. */
export interface View {
  /** Content pixels per screen pixel. 1 means one texel is one CSS pixel. */
  scale: number;
  /** Screen-space offset of the content's origin. */
  x: number;
  y: number;
}

export interface Size {
  w: number;
  h: number;
}

/**
 * As far in as the view will go.
 *
 * At 32× one texel is a 32px block, which is the scale at which you can place a
 * single pixel on a seam edge by eye. Further in stops being useful and starts
 * being a way to get lost.
 */
export const MAX_SCALE = 32;

/**
 * As far out as the view will go, relative to the fit.
 *
 * Half the fit scale, so there is somewhere to go "out" to from fitted — enough
 * to see the whole template with room around it, and not so far that the canvas
 * becomes a stamp in the middle of an empty pane.
 */
export const MIN_SCALE_OF_FIT = 0.5;

/** Breathing room left around the canvas when fitting, in screen pixels. */
const FIT_MARGIN = 16;

/**
 * The scale at which the whole canvas is visible.
 *
 * The smaller of the two ratios, so the constrained axis decides — the usual
 * bug is taking the wrong one and cropping the long side.
 */
export function fitScale(content: Size, port: Size, margin = FIT_MARGIN): number {
  const w = Math.max(1, port.w - margin * 2);
  const h = Math.max(1, port.h - margin * 2);
  if (content.w <= 0 || content.h <= 0) return 1;
  return Math.min(w / content.w, h / content.h);
}

/** Keep a scale within what fitting allows and what is useful. */
export function clampScale(scale: number, content: Size, port: Size): number {
  const fit = fitScale(content, port);
  return Math.min(MAX_SCALE, Math.max(fit * MIN_SCALE_OF_FIT, scale));
}

/**
 * Zoom about a point, leaving whatever is under it where it is.
 *
 * The one piece of arithmetic a user notices instantly when it is wrong: zoom
 * about the centre instead and the thing you were looking at slides away, so
 * every zoom costs a pan to correct. Anchoring is what makes wheel-zoom and
 * pinch feel like the same gesture.
 *
 * `sx`/`sy` are in screen space, relative to the same origin as `view.x/y` —
 * i.e. the viewport element, not the page.
 */
export function zoomAt(view: View, nextScale: number, sx: number, sy: number): View {
  // The content point currently under the cursor.
  const cx = (sx - view.x) / view.scale;
  const cy = (sy - view.y) / view.scale;
  // Put it back under the cursor at the new scale.
  return { scale: nextScale, x: sx - cx * nextScale, y: sy - cy * nextScale };
}

/**
 * One axis of the pan limit.
 *
 * Two regimes, and they want opposite things. Zoomed out, the content is
 * smaller than the pane and there is nothing to pan — so it is centred, which
 * is also what stops it drifting into a corner when the window is resized.
 * Zoomed in, panning is the point, but the content must still cover the pane:
 * clamping to `[port - length, 0]` is what prevents the dead grey margin that
 * makes an editor feel like it has lost the document.
 */
export function clampAxis(offset: number, length: number, port: number): number {
  if (length <= port) return (port - length) / 2;
  return Math.min(0, Math.max(port - length, offset));
}

/** Both axes, plus the scale, brought inside their limits together. */
export function clampView(view: View, content: Size, port: Size): View {
  const scale = clampScale(view.scale, content, port);
  return {
    scale,
    x: clampAxis(view.x, content.w * scale, port.w),
    y: clampAxis(view.y, content.h * scale, port.h),
  };
}

/** The view that shows everything, centred. */
export function fitView(content: Size, port: Size): View {
  const scale = fitScale(content, port);
  return clampView({ scale, x: 0, y: 0 }, content, port);
}

/**
 * A wheel event's delta, in something like pixels.
 *
 * Three problems, all of them browser rather than app:
 *
 *   **`deltaMode`.** Firefox reports lines and, rarely, pages. The multipliers
 *   are the conventional ones — a line is about 8px, a page about 24 lines.
 *
 *   **Runaway magnitudes.** A hard trackpad flick or a mouse with a free-spin
 *   wheel can report hundreds in one event, which as a zoom factor is a jump
 *   from fitted to maximum in a single tick. Capped rather than scaled, so a
 *   normal notch still feels the same.
 *
 *   **Shift-swapped axes.** A horizontal scroll on a mouse with one wheel
 *   arrives as `shiftKey` with the delta on Y, which would otherwise read as a
 *   vertical pan.
 */
export function wheelDelta(e: {
  deltaX: number;
  deltaY: number;
  deltaMode: number;
  shiftKey: boolean;
}): { dx: number; dy: number } {
  const unit = e.deltaMode === 1 ? 8 : e.deltaMode === 2 ? 8 * 24 : 1;
  const cap = (v: number) => Math.max(-CAP, Math.min(CAP, v * unit));
  let dx = cap(e.deltaX);
  let dy = cap(e.deltaY);
  // One-wheel mice send a shifted vertical scroll to mean horizontal.
  if (e.shiftKey && dx === 0) {
    dx = dy;
    dy = 0;
  }
  return { dx, dy };
}

/** Per event, after `deltaMode` conversion. Roughly one notch. */
const CAP = 24;

/** How briskly a wheel or pinch zooms. Tuned so one notch is a visible step. */
const ZOOM_SPEED = 1.6;

/**
 * A scale multiplier for a zoom delta.
 *
 * Deliberately asymmetric so that zooming in and back out by the same amount
 * returns to exactly where it started: `factor(d) * factor(-d) === 1`. The
 * obvious `1 - d/100` does not have that property, and the drift it causes is
 * the kind that is only noticed after a minute of fiddling, as "the zoom is
 * slightly broken".
 */
export function zoomFactor(dy: number, speed = ZOOM_SPEED): number {
  const t = (speed * dy) / 100;
  return dy <= 0 ? 1 - t : 1 / (1 + t);
}

/** Screen point to content point. The inverse the pointer paths need. */
export function toContent(view: View, sx: number, sy: number): { x: number; y: number } {
  return { x: (sx - view.x) / view.scale, y: (sy - view.y) / view.scale };
}

/** True when two views differ enough to be worth re-rendering. */
export function viewsDiffer(a: View, b: View): boolean {
  return (
    Math.abs(a.scale - b.scale) > 1e-6 || Math.abs(a.x - b.x) > 0.5 || Math.abs(a.y - b.y) > 0.5
  );
}

/** The CSS the wrapper needs to realise a view. `transform-origin` is 0 0. */
export function transformOf(view: View): string {
  return `translate(${view.x}px, ${view.y}px) scale(${view.scale})`;
}
